// Companion-state resolver shared by the R9 screens (01, 03, 05, 06, 07).
//
// Each screen owns its own data signals (fetch state, envelope.degraded,
// adapter pending flag, feature-gate state, active-call signal). This
// helper only normalises those signals into one BoundaryState so every
// screen reaches the DataBoundary through the same precedence rules:
//
//   mid-call > blocked > error > degraded > loading > empty > default
//
// Rationale for the ordering:
//   - mid-call wins because the rep is on the phone right now; the
//     screen must stop issuing writes until the call settles.
//   - blocked wins over error because a disabled gate explains the
//     refusal honestly; retrying is pointless until the gate flips.
//   - error wins over degraded because a hard fetch failure is more
//     disruptive than partial data, and the rep deserves the direct
//     message.
//   - degraded is a readable fallback for aggregator envelopes whose
//     `degraded === true` region list has substance.
//   - loading precedes empty so the empty state only lands after the
//     fetch resolved.
//
// The helper is intentionally a plain function (not a hook) so it can
// be called inside components or tested without a React runtime. Each
// screen wraps its own useMemo so React reruns the resolver only when
// its inputs change.
//
// This file holds NO business logic beyond the ordering. Any screen
// that needs a different precedence or extra state kinds should
// introduce them explicitly rather than branching here.

import type { BoundaryState } from './DataBoundary.js';

export interface ScreenCompanionSignals {
  /** First-fetch in flight, no data yet. */
  isLoading?: boolean;
  loadingLabel?: string;
  /** Fetch resolved but the primary record set is empty. */
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyBody?: string;
  /** Hard fetch failure. Use the real error code from the envelope. */
  error?: {
    title: string;
    detail?: string;
    errorCode?: string;
  } | null;
  /** Aggregator envelope reported `degraded === true` for one or more regions. */
  degraded?: {
    title: string;
    regions: Array<{ region: string; reason: string }>;
  } | null;
  /** Feature-gate off or permission denied for this surface. */
  blocked?: {
    title: string;
    reason: string;
    gateName?: string;
  } | null;
  /** Live call is active and the screen must enter call-context mode. */
  midCall?: {
    label: string;
    actionName?: string;
  } | null;
}

/**
 * Collapse the signal set into one BoundaryState using the documented
 * precedence. Returns `{ kind: 'default' }` when no signal applies.
 */
export function resolveCompanionState(signals: ScreenCompanionSignals): BoundaryState {
  if (signals.midCall) {
    return signals.midCall.actionName
      ? { kind: 'midcall', label: signals.midCall.label, actionName: signals.midCall.actionName }
      : { kind: 'midcall', label: signals.midCall.label };
  }
  if (signals.blocked) {
    return signals.blocked.gateName
      ? {
          kind: 'blocked',
          title: signals.blocked.title,
          reason: signals.blocked.reason,
          gateName: signals.blocked.gateName,
        }
      : { kind: 'blocked', title: signals.blocked.title, reason: signals.blocked.reason };
  }
  if (signals.error) {
    const base: BoundaryState = { kind: 'error', title: signals.error.title };
    if (signals.error.detail) (base as { detail?: string }).detail = signals.error.detail;
    if (signals.error.errorCode) (base as { errorCode?: string }).errorCode = signals.error.errorCode;
    return base;
  }
  if (signals.degraded) {
    return {
      kind: 'degraded',
      title: signals.degraded.title,
      regions: signals.degraded.regions,
    };
  }
  if (signals.isLoading) {
    return signals.loadingLabel
      ? { kind: 'loading', label: signals.loadingLabel }
      : { kind: 'loading' };
  }
  if (signals.isEmpty) {
    const base: BoundaryState = {
      kind: 'empty',
      title: signals.emptyTitle ?? 'No records yet',
    };
    if (signals.emptyBody) (base as { body?: string }).body = signals.emptyBody;
    return base;
  }
  return { kind: 'default' };
}

/**
 * Convenience helper that returns true if a non-default state applies.
 * Screens use this to decide whether to render companion content or
 * the full default layout; the default layout must stay pixel-identical
 * to the approved HTML, so a screen never wraps its own default body
 * in boundary chrome.
 */
export function isCompanionActive(signals: ScreenCompanionSignals): boolean {
  return !!(
    signals.midCall ||
    signals.blocked ||
    signals.error ||
    signals.degraded ||
    signals.isLoading ||
    signals.isEmpty
  );
}
